import { forwardRef, useState, type InputHTMLAttributes, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from '../cn';
import { Field, Input } from './field';

interface PasswordFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: ReactNode;
  error?: string | undefined;
  hint?: ReactNode;
}

function EyeIcon({ off }: { off: boolean }) {
  return (
    <svg aria-hidden viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="size-4">
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" />
      <circle cx="12" cy="12" r="3" />
      {off && <path d="m3 3 18 18" />}
    </svg>
  );
}

/** Campo de senha com botão de mostrar/esconder e aviso de Caps Lock ligado. */
export const PasswordField = forwardRef<HTMLInputElement, PasswordFieldProps>(function PasswordField(
  { label, error, hint, id, className, onKeyDown, onKeyUp, onBlur, ...rest },
  ref,
) {
  const [visible, setVisible] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const checkCaps = (e: KeyboardEvent<HTMLInputElement>) => {
    if (typeof e.getModifierState === 'function') setCapsLock(e.getModifierState('CapsLock'));
  };
  return (
    <Field label={label} error={error} hint={hint} id={id} className={className}>
      {(a11y) => (
        <>
          <div className="relative">
            <Input
              ref={ref}
              {...a11y}
              {...rest}
              type={visible ? 'text' : 'password'}
              className="pr-11"
              onKeyDown={(e) => {
                checkCaps(e);
                onKeyDown?.(e);
              }}
              onKeyUp={(e) => {
                checkCaps(e);
                onKeyUp?.(e);
              }}
              onBlur={(e) => {
                setCapsLock(false);
                onBlur?.(e);
              }}
            />
            <button
              type="button"
              aria-label={visible ? 'Esconder senha' : 'Mostrar senha'}
              aria-pressed={visible}
              aria-controls={a11y.id}
              title={visible ? 'Esconder senha' : 'Mostrar senha'}
              onClick={() => setVisible((v) => !v)}
              className={cn(
                'absolute inset-y-0 right-1 my-auto grid size-8 cursor-pointer place-items-center rounded-full text-muted transition',
                'hover:bg-mist hover:text-ink focus-visible:outline-2 focus-visible:outline-violet',
              )}
            >
              <EyeIcon off={visible} />
            </button>
          </div>
          {capsLock && (
            <p role="status" className="text-xs font-medium text-warning">
              Caps Lock está ativado.
            </p>
          )}
        </>
      )}
    </Field>
  );
});
